import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Title } from '~components/Typography';
import { NsfwReason } from '~features/annals/enums/nsfwReason';
import annalsUserSettings from '~features/annals/hooks/userSettings';
import {
	useSafeModeRedirect,
	useSwitchSafeModeWithAgeValidation,
} from '~features/nsfw';
import { Toaster } from '~features/toaster';
import { formToField } from '~features/userInfo/model/constants';
import { useSession } from '~hooks/useSession';
import { useTranslations } from '~i18n/hooks';
import Close from '~public/assets/icons/close.svg';
import { userApi } from '~services/api/user';
import { TabMenu } from '~shared/ui/molecules/TabMenu';
import { isSafeApp } from '~shared/utils/host';

import type { UserInfoForm, UserInfoTab } from '../../model/types';
import { Actions } from '../../ui/Actions';
import { TabContent } from '../../ui/TabContent';
import {
	CloseButton,
	styledTabs,
	StyledWrapper,
	UserInfoContent,
} from './styles';

const useTabClickAnnals = () => {
	const sendAnnalsProfileClick = annalsUserSettings.useProfileClicked();
	const sendAnnalsContentClick = annalsUserSettings.useContentClicked();
	const sendAnnalsSecurityClick = annalsUserSettings.useSecurityClicked();

	const annals = useMemo(
		() => ({
			profile: sendAnnalsProfileClick,
			content: sendAnnalsContentClick,
			security: sendAnnalsSecurityClick,
		}),
		[sendAnnalsContentClick, sendAnnalsProfileClick, sendAnnalsSecurityClick]
	);

	return annals;
};

export const Wrapper = ({
	kinks,
	defaultValues,
	onClose,
}: {
	defaultValues: UserInfoForm;
	kinks: { id: string; text: string }[];
	onClose: () => void;
}) => {
	const t = useTranslations('SettingsMenu');

	const sortedKinks = useMemo(
		() =>
			[...kinks].sort((a, b) => {
				if (defaultValues.kinks.includes(a.id)) {
					return -1;
				} else if (defaultValues.kinks.includes(b.id)) {
					return 1;
				} else {
					return 0;
				}
			}),

		// update it only when modal opens again
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[kinks]
	);

	const { userId } = useSession();
	const formT = useTranslations('Forms');
	const updateSafeMode = useSwitchSafeModeWithAgeValidation();
	const { redirectToAppropriateDomain } = useSafeModeRedirect();
	const [updateInfo] = userApi.useEditUserInfoMutation();
	const [updateUserProfile] = userApi.useSetUserProfileMutation();
	const [updateUserProfilePreferences] =
		userApi.useSetUserProfilePreferencesMutation();

	const tabClickAnnals = useTabClickAnnals();
	const sendAnnalsCloseClick = annalsUserSettings.useCloseClicked();
	const sendAnnalsProfileSave = annalsUserSettings.useProfileSaveClicked();
	const sendAnnalsContentSave = annalsUserSettings.useContentSaveClicked();
	const sendAnnalsSecuritySave = annalsUserSettings.useSecuritySaveClicked();

	const tabs: UserInfoTab[] = useMemo(
		() => [
			{
				value: 'profile',
				label: t('firstTabTitle'),
				content: ['avatar', 'nickname', 'bio'],
			},
			{
				value: 'content',
				label: t('secondTabTitle'),
				content: ['isNSFW', 'gender', 'into', 'age', 'kinks'],
			},
			{
				value: 'security',
				label: t('thirdTabTitle'),
				content: ['privateData'],
			},
		],
		[t]
	);
	const [tab, setTab] = useState<UserInfoTab>(tabs[0]);

	const form = useForm<UserInfoForm>({
		mode: 'onChange',
		defaultValues,
	});

	useEffect(() => {
		form.reset();
	}, [form]);

	const annalsSave = useCallback(
		(data: UserInfoForm) => {
			if (tab.value === 'profile') {
				sendAnnalsProfileSave({
					name: data.name,
					bio: data.bio,
				});
			}
			if (tab.value === 'content') {
				sendAnnalsContentSave({
					gender: data.gender,
					looking_for_gender: data.into.join(';'),
					preferable_age: `${data.ageMin}-${data.ageMax}`,
					fantasies: data.kinks.join(';'),
				});
			}
			if (tab.value === 'security') {
				sendAnnalsSecuritySave();
			}
		},
		[
			sendAnnalsContentSave,
			sendAnnalsProfileSave,
			sendAnnalsSecuritySave,
			tab.value,
		]
	);

	const onSaveError = useCallback(
		(e: unknown) => {
			const error =
				e && typeof e === 'object' && 'data' in e
					? (e.data as { code: number }).code
					: undefined;
			const nickError = error === 2;
			const nickUniqueError = error === 1;
			const bioError = error === 4;

			if (nickError || bioError || nickUniqueError) {
				if (nickError) {
					form.setError('nickname', {
						message: formT('validationSafetyError'),
					});
				} else if (bioError) {
					form.setError('bio', {
						message: formT('validationSafetyError'),
					});
				} else {
					form.setError('nickname', {
						message: formT('nickIsNotUniqueError'),
					});
				}

				setTab(tabs[0]);
			} else {
				form.setError('root', {
					message: formT('submitError'),
				});
			}
		},
		[form, formT, tabs]
	);

	const onSaveSuccess = useCallback(
		async (data: UserInfoForm) => {
			const { dirtyFields } = form.formState;
			const promises = [];

			const userProfilePreferencesChanges = {
				ageMax: data.ageMax,
				ageMin: data.ageMin,
				genders: data.into,
				kinks: data.kinks,
			};
			const userInfoChanges = {
				name: data.name,
				birthday: data.birthday,
				gender: data.gender,
			};

			if (dirtyFields.name || dirtyFields.birthday || dirtyFields.gender) {
				promises.push(
					updateInfo({ id: userId, data: userInfoChanges }).unwrap()
				);
			}

			if (
				dirtyFields.ageMin ||
				dirtyFields.ageMax ||
				dirtyFields.into ||
				dirtyFields.kinks
			) {
				promises.push(
					updateUserProfilePreferences({
						userId,
						data: userProfilePreferencesChanges,
					}).unwrap()
				);
			}

			if (!isSafeApp && dirtyFields.isNSFW) {
				promises.push(
					updateSafeMode(!data.isNSFW, {
						nsfwReason: NsfwReason.Profile,
						hideDialog: true,
					}).then(() => {
						redirectToAppropriateDomain();
					})
				);
			}

			try {
				await Promise.all(promises);

				form.reset(data);
				Toaster.showSuccess({ titleKey: 'updateSuccessTitle' });
			} catch {
				form.setError('root', {
					message: formT('submitError'),
				});
			}
		},
		[
			form,
			formT,
			redirectToAppropriateDomain,
			updateInfo,
			updateSafeMode,
			updateUserProfilePreferences,
			userId,
		]
	);

	const onSubmit = useCallback(
		async (data: UserInfoForm) => {
			annalsSave(data);
			const { dirtyFields } = form.formState;

			const userProfileChanges = {
				bio: data.bio,
				nick: data.nickname,
			};

			if (dirtyFields.nickname || dirtyFields.bio) {
				try {
					await updateUserProfile({
						userId,
						data: userProfileChanges,
					}).unwrap();
				} catch (e: unknown) {
					onSaveError(e);

					return;
				}
			}

			await onSaveSuccess(data);
		},
		[
			annalsSave,
			form.formState,
			onSaveError,
			onSaveSuccess,
			updateUserProfile,
			userId,
		]
	);

	const onInvalid = (errors: typeof form.formState.errors) => {
		const errorKeys = Object.keys(errors) as (keyof UserInfoForm)[];
		const errorFields = errorKeys.map((key) => formToField[key]);
		const sortedTabs = [...tabs].sort((a) => (a.value === tab.value ? -1 : 0));
		const errorTab = sortedTabs.find((tab) =>
			tab.content.some((field) => errorFields.includes(field))
		);
		if (errorTab) {
			setTab(errorTab);
		}
	};

	const handleClose = useCallback(() => {
		sendAnnalsCloseClick();
		onClose();
	}, [onClose, sendAnnalsCloseClick]);

	return (
		<UserInfoContent onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
			<CloseButton
				type="button"
				size="M"
				leftIcon={<Close />}
				onClick={handleClose}
			/>
			<Title align="left">Settings</Title>
			<TabMenu
				className={styledTabs}
				size="S"
				active={tab.value}
				tabs={tabs}
				onChange={(value: string) => {
					const newTab = tabs.find((tab) => tab.value === value);
					if (newTab) {
						setTab(newTab);
						tabClickAnnals[newTab.value]();
					}
				}}
			/>
			<StyledWrapper>
				<TabContent kinks={sortedKinks} form={form} tab={tab} />
			</StyledWrapper>
			<Actions
				onClose={onClose}
				isUserId={tab.value !== tabs[2].value}
				tab={tab.value}
				form={form}
			/>
		</UserInfoContent>
	);
};
